import { GetUserTasksByUserIDResponseRow } from '@weave/schema-gen-ts/dist/schemas/insys/onboarding/v1/onboarding-tasks/onboarding_tasks.pb';
import { OnboardingProgressStatus } from '@weave/schema-gen-ts/dist/shared/insys/activation/onboarding.pb';
import { MutationOptions, UseQueryOptions } from 'react-query';
import { getDecodedWeaveToken, getUser } from '@frontend/auth-helpers';
import { HttpError } from '@frontend/fetch';
import { useMutation, useQuery } from '@frontend/react-query-helpers';
import { SchemaOnboardingProgressService } from '@frontend/schema';
import { sentry } from '@frontend/tracking';
import { getTasksByUserId, getLatestOnboardingProgressByLocationID, publishOnboardingEvent } from './api';
import {
  IsUserSetupCompleteRequest,
  IsUserSetupCompleteResponse,
  PublishOnboardingEventRequest,
  ResendUserInviteRequest,
  ResendUserInviteResponse,
} from './types';

export const onboardingModulesQueryKeys = {
  base: ['onboarding-modules'] as const,
  modulesAndTasks: (locationId: string) => [...onboardingModulesQueryKeys.base, 'modules-and-tasks', locationId],
  taskSteps: (taskId: number) => [...onboardingModulesQueryKeys.base, taskId, 'task-steps'],
  dashboardTaskPercentage: () => [...onboardingModulesQueryKeys.base, 'dashboard-task-percentage'],
  latestOnboardingProgressStatus: (locationId: string) => [
    ...onboardingModulesQueryKeys.base,
    'latest-onboarding-progress-status',
    locationId,
  ],
  fetchUserSetupStatus: (email: string) => [...onboardingModulesQueryKeys.base, 'user-setup-status', email],
};

export const useFetchUserTasksQuery = (
  locationId: string,
  options?: UseQueryOptions<GetUserTasksByUserIDResponseRow[]>
) => {
  const user = getUser();

  const tokenUserId = getDecodedWeaveToken()?.user_id ?? '';
  const mismatchUserId = user?.userID !== tokenUserId;
  if (mismatchUserId) {
    sentry.warn({
      error: 'UserId mismatch. LocalStorage UserId different than token user_id. ID: ONB-jd9487rh',
      topic: 'onboarding',
      team: 'fe-clm',
      addContext: {
        name: 'details',
        context: {
          userId: user?.userID,
          tokenUserId,
        },
      },
    });
  }

  return useQuery({
    queryKey: [locationId, tokenUserId, 'user-tasks'],
    queryFn: () => {
      return getTasksByUserId(tokenUserId);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
    enabled: !!locationId && !!tokenUserId && (options?.enabled ?? true),
  });
};

export const useGetLatestOnboardingProgressByLocationID = (
  locationId: string,
  options?: UseQueryOptions<OnboardingProgressStatus>
) => {
  return useQuery({
    queryKey: onboardingModulesQueryKeys.latestOnboardingProgressStatus(locationId),
    queryFn: () => getLatestOnboardingProgressByLocationID(locationId),
    ...options,
    enabled: !!locationId && (options?.enabled ?? true),
  });
};

export const useResendUserInviteEmail = (
  options?: MutationOptions<ResendUserInviteResponse, unknown, ResendUserInviteRequest>
) => {
  return useMutation({
    mutationFn: (request: ResendUserInviteRequest) => {
      return SchemaOnboardingProgressService.ResendUserInviteEmail(request, {
        skipValidation: true,
        headers: { '-Authorization': '' },
      });
    },
    ...options,
  });
};

export const useIsUserSetupComplete = (
  request: IsUserSetupCompleteRequest,
  options?: UseQueryOptions<IsUserSetupCompleteResponse>
) => {
  return useQuery({
    queryKey: onboardingModulesQueryKeys.fetchUserSetupStatus(request?.userEmail),
    queryFn: () =>
      SchemaOnboardingProgressService.IsUserSetupComplete(request, {
        skipValidation: true,
        headers: { '-Authorization': '' },
      }),
    retry: false,
    ...options,
    enabled: !!request.userEmail && (options?.enabled ?? true),
  });
};

export const usePublishOnboardingEvent = (
  options?: MutationOptions<unknown, HttpError, PublishOnboardingEventRequest>
) => {
  return useMutation({
    mutationFn: publishOnboardingEvent,
    ...options,
  });
};

export const usePublishMultipleOnboardingEvents = (
  options?: MutationOptions<unknown, HttpError, PublishOnboardingEventRequest[]>
) => {
  return useMutation({
    mutationFn: async (requests: PublishOnboardingEventRequest[]) => {
      return Promise.all(requests.map((request) => publishOnboardingEvent(request)));
    },
    ...options,
  });
};
